import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { PeriodValue } from "features/common/Interfaces";
import {
  IClassFormativeData,
  IGradeFormativeData,
  IStudentFormativeData,
  ITeacherFormativeData,
} from "views/Secure/Reports/Utils/FormativeHelpers";

export enum ReportType {
  Teachers_Report = "teachers",
  Teacher_Report = "teacher",
  Class_Report = "class",
  Grades_Report = "grades",
  Grade_Report = "grade",
}

// Keys will be received by API response
export const domains = {
  WRITING: "Writing",
  READING: "Reading",
  LISTENING: "Listening",
  LANGUAGE: "Language",
};

export const colors = {
  Reading: "rgb(68, 183, 253)",
  Writing: "rgb(90, 107, 199)",
  Listening: "rgb(134, 83, 203)",
  Language: "rgb(201, 97, 206)",
  bgColor: "rgb(234, 234, 234)",
};

export type ChartDataType = {
  [key: string]: number;
} & { selectedItem: string; title: string; xAxisLabel: string };
export type DataKeysType = {
  label: string;
  color: string;
};
export interface IProgressData {
  chartData: ChartDataType[];
  dataKeys: DataKeysType[];
}
export interface IDomainFormativeData {
  domainLanguageTitle: string;
  domainLanguageCode: string;
  totalAssignedStudents: number;
  submittedAssessments: number;
  avgCompletedStudents: number;
  evaluatedAssessments: number;
  avgScore: number;
}
export interface IFormativeData extends IDomainFormativeData {
  teachers?: ITeacherFormativeData[];
  classes?: IClassFormativeData[];
  grades?: IGradeFormativeData[];
  students?: IStudentFormativeData[];
}

interface IReportsState {
  isPrinting: boolean;
  isAssignmentUsageLoading: boolean;
  breadCrumbLabels: any[];
  reportType: ReportType;
  selectedTeacherCode?: string;
  selectedClassCode?: string;
  selectedGradeCode?: string;
  progressData?: IProgressData;
  formativeData?: IFormativeData[];
  isProgressLoading: boolean;
  isFormativeLoading: boolean;
  selectedRange: { value: PeriodValue; type: string };
  generationTime?: string;
}

export const enum UrlObject {
  TeachersUrl = "teachers-reports",
  GradesUrl = "grades-reports",
}

export interface IReportTypeOption {
  label: string;
  value: PeriodValue;
  type: string;
}
export const initialOptions: IReportTypeOption[] = [
  {
    label: "Academic Year",
    value: PeriodValue.ALL_YEAR,
    type: PeriodValue.ALL_YEAR,
  },
  {
    label: "Last Week",
    value: PeriodValue.LAST_WEEK,
    type: PeriodValue.LAST_WEEK,
  },
];
const initialState: IReportsState = {
  isPrinting: false,
  isAssignmentUsageLoading: true,
  breadCrumbLabels: [],
  reportType: ReportType.Teachers_Report,
  isProgressLoading: true,
  isFormativeLoading: true,
  selectedRange: initialOptions[0],
};
export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    printingSet(state: IReportsState, action: PayloadAction<boolean>) {
      state.isPrinting = action.payload;
    },
    assignmentUsageLoadingSet(
      state: IReportsState,
      action: PayloadAction<boolean>
    ) {
      state.isAssignmentUsageLoading = action.payload;
    },
    reportTypeSet(state: IReportsState, action: PayloadAction<ReportType>) {
      state.reportType = action.payload;
    },
    progressDataSet(
      state: IReportsState,
      action: PayloadAction<IProgressData>
    ) {
      state.progressData = action.payload;
    },
    formativeDataSet(
      state: IReportsState,
      action: PayloadAction<IFormativeData[]>
    ) {
      state.formativeData = action.payload;
    },
    breadCrumbLabelsSet(state: IReportsState, action: PayloadAction<any>) {
      state.breadCrumbLabels = action.payload;
    },
    selectedTeacherCodeSet(
      state: IReportsState,
      action: PayloadAction<string | undefined>
    ) {
      state.selectedTeacherCode = action.payload;
    },
    selectedClassCodeSet(
      state: IReportsState,
      action: PayloadAction<string | undefined>
    ) {
      state.selectedClassCode = action.payload;
    },
    selectedGradeCodeSet(
      state: IReportsState,
      action: PayloadAction<string | undefined>
    ) {
      state.selectedGradeCode = action.payload;
    },
    selectedRangeSet(
      state: IReportsState,
      action: PayloadAction<{ value: PeriodValue; type: string }>
    ) {
      state.selectedRange = action.payload;
    },
    isFormativeLoadingSet(
      state: IReportsState,
      action: PayloadAction<boolean>
    ) {
      state.isFormativeLoading = action.payload;
    },
    isProgressLoadingSet(state: IReportsState, action: PayloadAction<boolean>) {
      state.isProgressLoading = action.payload;
    },
    generationTimeSet(state: IReportsState, action: PayloadAction<string>) {
      state.generationTime = action.payload;
    },
  },
});

export const selectIsProgressLoading = (state: RootState) =>
  state.reports.isProgressLoading;
export const selectIsFormativeLoading = (state: RootState) =>
  state.reports.isFormativeLoading;
export const selectSelectedRange = (state: RootState) =>
  state.reports.selectedRange;
export const selectSelectedGradeCode = (state: RootState) =>
  state.reports.selectedGradeCode;
export const selectSelectedClassCode = (state: RootState) =>
  state.reports.selectedClassCode;
export const selectSelectedTeacherCode = (state: RootState) =>
  state.reports.selectedTeacherCode;
export const selectBreadCrumbLabels = (state: RootState) =>
  state.reports.breadCrumbLabels;
export const selectFormativeData = (state: RootState) =>
  state.reports.formativeData;
export const selectProgressData = (state: RootState) =>
  state.reports.progressData;
export const selectReportType = (state: RootState) => state.reports.reportType;
export const selectIsPrinting = (state: RootState) => state.reports.isPrinting;
export const selectIsAssignmentUsageLoading = (state: RootState) =>
  state.reports.isAssignmentUsageLoading;
export const selectGenerationTime = (state: RootState) =>
  state.reports.generationTime;

export const {
  printingSet,
  assignmentUsageLoadingSet,
  breadCrumbLabelsSet,
  formativeDataSet,
  isFormativeLoadingSet,
  isProgressLoadingSet,
  progressDataSet,
  reportTypeSet,
  selectedClassCodeSet,
  selectedGradeCodeSet,
  selectedRangeSet,
  selectedTeacherCodeSet,
  generationTimeSet,
} = reportsSlice.actions;

export default reportsSlice.reducer;
